import { Link, Outlet, useParams, useRouterState } from '@tanstack/react-router';
import { BookOpen, CalendarRange, FileDown, ListChecks, NotebookText, Ticket, Wallet } from 'lucide-react';
import { cx } from '../kit/cx.ts';
import { SegmentedToggle } from '../kit/index.ts';
import t from '../kit/type.module.css';
import { today } from '../model/trip.ts';
import { useTripCards } from './data.ts';
import s from './Shell.module.css';
import { SimulationBand, useTripClock } from './SimulationBand.tsx';
import { setTheme, type Theme, useTheme } from './theme.ts';

/** The W-01 sidebar: trips (now, upcoming, past), the trip's sections, sync state. */
export function Shell({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  const trips = useTripCards();
  const params = useParams({ strict: false }) as { tripId?: string };
  const path = useRouterState({ select: (r) => r.location.pathname });
  useTripClock(); // the sidebar and the page follow the simulated clock
  const now = today();
  const all = trips.cards;
  const groups: [string, typeof all][] = [
    [
      'Now travelling',
      all.filter((x) => !x.archived && x.start && x.start <= now && (x.end ?? x.start) >= now),
    ],
    ['Upcoming', all.filter((x) => !x.archived && (!x.start || x.start > now))],
    ['Past', all.filter((x) => x.archived || (x.end != null && x.end < now))],
  ];
  const meta = (x: (typeof all)[number]) => {
    if (!x.start) return `${x.code} · No dates`;
    const day = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);
    if (x.start > now) return `${x.code} · In ${day(now, x.start)} days`;
    if (x.end && x.end < now) return `${x.code} · ${x.start.slice(0, 4)}`;
    const n = String(day(x.start, now) + 1).padStart(2, '0');
    const of = String(day(x.start, x.end ?? x.start) + 1).padStart(2, '0');
    return `${x.code} · Day ${n} / ${of}`;
  };
  return (
    <div className={s.shell}>
      <nav className={s.side} aria-label="Trips and sections">
        <div className={cx(s.brand, t.displayL)}>
          Pathfinder
          <span aria-hidden="true" />
        </div>
        {groups.map(([name, list]) =>
          list.length === 0 ? null : (
            <div key={name}>
              <div className={cx(s.group, t.label)}>{name}</div>
              {list.map((x) => (
                <Link
                  key={x.id}
                  to="/trips/$tripId"
                  params={{ tripId: x.id }}
                  className={cx(s.trip, x.id === params.tripId && s.active)}
                >
                  <div className={t.titleM}>{x.title}</div>
                  <div className={cx(s.tripMeta, t.readoutS)}>{meta(x)}</div>
                </Link>
              ))}
            </div>
          ),
        )}
        {params.tripId && (
          <div className={s.nav}>
            {(
              [
                ['Plan', '/trips/$tripId', ListChecks],
                ['Timeline', '/trips/$tripId/timeline', CalendarRange],
                ['Bookings', '/trips/$tripId/bookings', Ticket],
                ['Budget', '/trips/$tripId/budget', Wallet],
                ['Info', '/trips/$tripId/info', NotebookText],
                ['Journal', '/trips/$tripId/journal', BookOpen],
                ['Export', '/trips/$tripId/export', FileDown],
              ] as const
            ).map(([label, to, Icon]) => (
              <Link
                key={label}
                to={to}
                params={{ tripId: params.tripId as string }}
                className={cx(path === to.replace('$tripId', params.tripId as string) && s.active)}
              >
                <Icon size={18} aria-hidden="true" />
                <span className={t.body}>{label}</span>
              </Link>
            ))}
          </div>
        )}
        <ThemeSetting />
        <div className={cx(s.foot, t.micro)}>
          <span title={email}>{email.split('@')[0]}</span>
          <button type="button" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </nav>
      <main className={s.main}>
        <SimulationBand />
        <Outlet />
      </main>
    </div>
  );
}

const THEMES: Theme[] = ['auto', 'light', 'dark'];

/** Auto (the system's), Light or Dark, for this browser. */
export function ThemeSetting() {
  const theme = useTheme();
  return (
    <div className={s.theme}>
      <SegmentedToggle
        label="Appearance"
        tone="inverse"
        options={['Auto', 'Light', 'Dark']}
        value={THEMES.indexOf(theme)}
        onChange={(i) => setTheme(THEMES[i] ?? 'auto')}
      />
    </div>
  );
}
